import { useEffect, useRef, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

function getWsUrl() {
  return `${API.replace(/^http/, 'ws')}/api/ws/todays-room`;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];
const PING_INTERVAL = 25000;

export function useTodaysRoom() {
  const [room, setRoom] = useState(null);
  const wsRef = useRef(null);
  const retriesRef = useRef(0);
  const pingRef = useRef(null);
  const mountedRef = useRef(true);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    try {
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        retriesRef.current = 0;
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current || event.data === 'pong') return;
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'init') {
            setRoom(msg.post || null);
          } else if (msg.type === 'update' && msg.post) {
            setRoom(msg.post);
          } else if (msg.type === 'remove') {
            setRoom(null);
          }
        } catch {}
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        const delay = RECONNECT_DELAYS[Math.min(retriesRef.current, RECONNECT_DELAYS.length - 1)];
        retriesRef.current += 1;
        setTimeout(() => { if (mountedRef.current) connect(); }, delay);
      };

      ws.onerror = () => ws.close();
    } catch {
      const delay = RECONNECT_DELAYS[Math.min(retriesRef.current, RECONNECT_DELAYS.length - 1)];
      retriesRef.current += 1;
      setTimeout(() => { if (mountedRef.current) connect(); }, delay);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    // Also fetch via HTTP as initial fallback
    fetch(`${API}/api/todays-room/current`)
      .then(r => r.json()).then(d => { if (mountedRef.current && d) setRoom(d); }).catch(() => {});
    connect();
    return () => {
      mountedRef.current = false;
      if (pingRef.current) clearInterval(pingRef.current);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [connect]);

  return room;
}
